import * as React from 'react'

import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

export type ValidationIssue = {
  type?: number
  code?: string
  message?: string
  forceSave?: boolean
}

const OPERATION_RESULT_ERROR = 2
const OPERATION_RESULT_WARNING = 1

export const readValidationIssues = (payload: any): ValidationIssue[] =>
  Array.isArray(payload)
    ? payload.filter(
        (r: any) =>
          r?.type === OPERATION_RESULT_ERROR ||
          r?.type === OPERATION_RESULT_WARNING,
      )
    : []

/**
 * Saves a trip the way WPF's TripViewModel.OnSave() does: every objection the
 * user cannot wave away stops the save with a plain acknowledgement, and the
 * ones WPF asks Yes/No about are offered as a question, retried with `force`
 * when accepted. Used by the editor's Save button and by the settings modal,
 * which in WPF are the same save.
 *
 * The trips-list operations do NOT go through this — WPF saves those without
 * validating at all (see BULK_SAVE in useBusinessTripContextMenuActions).
 */
export const useTripSaveIssues = () => {
  const { updateBusinessTrip } = useBusinessTripService()
  const { t } = useLocalize()
  const confirm = useConfirm()

  /** Translated code plus whatever detail the server attached (trip numbers, dates). */
  const describeIssue = React.useCallback(
    (issue: ValidationIssue): string => {
      const heading = t(`errorCode.${issue.code}`) || issue.code || ''
      return issue.message ? `${heading}\n${issue.message}` : heading
    },
    [t],
  )

  const confirmIssues = React.useCallback(
    async (issues: ValidationIssue[]): Promise<boolean> => {
      const isBlocking =
        issues.some((i) => i.type === OPERATION_RESULT_ERROR) ||
        !issues.every((i) => i.forceSave)
      const body = issues.map(describeIssue).join('\n\n')

      if (isBlocking) {
        await confirm({
          title: t('newDelegation.delegation-edit-failed-title'),
          message: body,
          confirmText: t('answer.ok') || 'OK',
          hideCancel: true,
        })
        return false
      }

      return confirm({
        title: t('common.warning'),
        message: body,
        confirmText: t('answer.yes') || 'Tak',
        cancelText: t('answer.no') || 'Nie',
      })
    },
    [confirm, describeIssue, t],
  )

  /** Resolves true once the trip is actually persisted. */
  const saveTrip = React.useCallback(
    async (args: {
      companyId: string
      tripId: string | number
      driverId?: string | number
      data: any
    }): Promise<boolean> => {
      const attempt = async (force: boolean): Promise<boolean> => {
        let result: any
        try {
          result = await updateBusinessTrip({ ...args, force })
        } catch (err: any) {
          // A refused save comes back as 400 with the OperationResult list as the
          // body; without unpacking it the user only saw a generic failure toast.
          const issues = readValidationIssues(err?.response?.data)
          if (!issues.length) throw err
          return handleIssues(issues, force)
        }

        const issues = readValidationIssues(result)
        if (!issues.length) return true
        return handleIssues(issues, force)
      }

      const handleIssues = async (
        issues: ValidationIssue[],
        force: boolean,
      ): Promise<boolean> => {
        if (force) {
          // Already retried with force and still refused — say so rather than
          // leaving the user staring at a button that did nothing.
          await confirm({
            title: t('newDelegation.delegation-edit-failed-title'),
            message: issues.map(describeIssue).join('\n\n'),
            confirmText: t('answer.ok') || 'OK',
            hideCancel: true,
          })
          return false
        }
        if (!(await confirmIssues(issues))) return false
        return attempt(true)
      }

      return attempt(false)
    },
    [confirm, confirmIssues, describeIssue, t, updateBusinessTrip],
  )

  return { saveTrip }
}
